import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, message } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import i18n, { t } from 'i18n';
import BillOfLading from './BillOfLading';
import BolFormEditor from './BolFormEditor';
import BolHandoverFormEditor from './BolHandoverFormEditor';
import { printBol } from './ts/bolPrintUtils';
import { BolDataV3, TaskGroupType } from './types/bolTypes';
import { isHandoverStyleBol, isHandoverThirdPartyBol } from './ts/bolUtils';

export type { BolDataV3 };

interface BolPreviewModalProps {
    open: boolean;
    onClose: () => void;
    initialBOLData: BolDataV3 | null;
    taskGroupType?: TaskGroupType;
}

const BolPreviewModal: React.FC<BolPreviewModalProps> = ({
    open,
    onClose,
    initialBOLData,
    taskGroupType
}) => {
    const [bolData, setBolData] = useState<BolDataV3 | null>(null);
    const previewScrollRef = useRef<HTMLDivElement>(null);
    const editorKeyRef = useRef(0);

    useEffect(() => {
        if (open && initialBOLData) {
            setBolData(initialBOLData);
            // 重置编辑组件 key，强制重新渲染
            editorKeyRef.current += 1;
            // 延迟重置预览区域滚动位置，确保 DOM 已渲染
            setTimeout(() => {
                if (previewScrollRef.current) {
                    previewScrollRef.current.scrollTop = 0;
                }
            }, 100);
        }
    }, [open, initialBOLData]);

    const validateBolData = (data: BolDataV3): string[] => {
        const errors: string[] = [];
        const requiredText = String(t('components.input.required'));
        const currentLang = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
        const space = currentLang === 'zh' ? '' : ' ';

        const isBlank = (value?: string) => !value || value.trim() === '';

        type ShipToKey = 'shipToAddress' | 'shipToCityStateZipcode' | 'shipToContactName' | 'shipToContactPhoneNumber';
        const shipToFields: Array<{ key: ShipToKey; label: string }> = [
            { key: 'shipToAddress', label: String(t('tms.task.bol.address')) },
            { key: 'shipToCityStateZipcode', label: String(t('tms.task.bol.cityStateZip')) },
            { key: 'shipToContactName', label: String(t('tms.task.bol.contactName')) },
            { key: 'shipToContactPhoneNumber', label: String(t('tms.task.bol.contactNumber')) }
        ];

        const shipToLabelBase = String(t('tms.task.bol.shipTo'));
        const stopLabelKeys = ['pickUp', 'stop1', 'stop2', 'stop3', 'dropOff'] as const;
        type StopLabelKey = (typeof stopLabelKeys)[number];
        const stopLabels: Record<StopLabelKey, string> = {
            pickUp: String(t('tms.task.bol.stopLabels.pickUp')),
            stop1: String(t('tms.task.bol.stopLabels.stop1')),
            stop2: String(t('tms.task.bol.stopLabels.stop2')),
            stop3: String(t('tms.task.bol.stopLabels.stop3')),
            dropOff: String(t('tms.task.bol.stopLabels.dropOff'))
        };
        const resolveStopLabel = (index: number, totalStops: number) => {
            if (index === 0) {
                return stopLabels.pickUp;
            }
            if (index === totalStops - 1) {
                return stopLabels.dropOff;
            }
            const intermediateKey = (`stop${Math.min(index, 3)}` as StopLabelKey);
            return stopLabels[intermediateKey] ?? `${shipToLabelBase}${space}${index + 1}`;
        };
        const isHandoverStyle = isHandoverStyleBol(data);
        const isHandoverThirdParty = isHandoverThirdPartyBol(data);
        data.stops.forEach((stop, index) => {
            // 交接样式：仅编辑/校验始发站，其余站点跳过（与交接编辑器一致）；三方任务走普通样式，逐站校验
            if (isHandoverStyle && index > 0) return;
            const stopIdentifier = resolveStopLabel(index, data.stops.length);
            shipToFields.forEach((field) => {
                // 交接+三方：非始发站的联系人姓名/电话非必填，打印前不校验
                if (isHandoverThirdParty && index > 0
                    && (field.key === 'shipToContactName' || field.key === 'shipToContactPhoneNumber')) return;
                const fieldValue = stop[field.key];
                if (isBlank(fieldValue)) {
                    errors.push(`${stopIdentifier}:${space}${field.label}${space}${requiredText}`);
                }
            });
        });

        // 司机名称/电话不支持编辑，也不再作为打印前必填项
        type CarrierFieldKey = 'trailerNumber' | 'licensePlate';
        const carrierFields: Array<{ key: CarrierFieldKey; label: string }> = [
            { key: 'trailerNumber', label: String(t('tms.task.bol.trailerNumber')) },
            { key: 'licensePlate', label: String(t('tms.task.bol.licensePlate')) }
        ];

        carrierFields.forEach((field) => {
            const fieldValue = data.carrier[field.key];
            if (isBlank(fieldValue)) {
                errors.push(`${space}${field.label}${space}${requiredText}`);
            }
        });

        return errors;
    };

    const handleFormChange = (updatedData: BolDataV3) => {
        setBolData(updatedData);
    };

    const handlePrint = () => {
        if (!bolData) return;

        const errors = validateBolData(bolData);

        if (errors.length > 0) {
            const errorList = errors.map((error, idx) => `${idx + 1}. ${error}`).join('\n');
            message.error({
                content: (
                    <div style={{ whiteSpace: 'pre-line', maxWidth: '600px', textAlign: 'left' }}>
                        {errorList}
                    </div>
                ),
                duration: 5,
            });
            return;
        }

        printBol([bolData]);
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            title={String(t('tms.task.bol.previewAndEdit'))}
            width={1600}
            centered
            afterOpenChange={(isOpen) => {
                if (isOpen) {
                    // Modal 打开后重置滚动位置
                    setTimeout(() => {
                        if (previewScrollRef.current) {
                            previewScrollRef.current.scrollTop = 0;
                        }
                    }, 100);
                }
            }}
            footer={[
                <Button key="cancel" variant="outlined" onClick={handleClose}>
                    {String(t('tms.task.buttons.cancel'))}
                </Button>,
                <Button
                    key="print"
                    type="primary"
                    icon={<PrinterOutlined />}
                    onClick={handlePrint}
                >
                    {String(t('tms.task.bol.print'))}
                </Button>
            ]}
        >
            {bolData && (
                <div className="flex flex-col gap-5 max-h-[83vh]">
                    <div className="flex gap-5 flex-1 overflow-y-hidden overflow-x-auto">
                        <div className="w-[900px] border border-gray-300 rounded bg-white flex flex-col overflow-hidden flex-shrink-0">
                            <h3 className="px-5 pt-3 pb-2 bg-white border-b border-gray-200 flex-shrink-0 whitespace-nowrap rounded-t">{String(t('tms.task.bol.preview'))}</h3>
                            <div ref={previewScrollRef} className="overflow-y-auto flex-1 rounded-b">
                                <div className="p-5">
                                    <BillOfLading bolData={bolData} />
                                </div>
                            </div>
                        </div>

                        {isHandoverStyleBol(bolData) ? (
                            <BolHandoverFormEditor
                                key={editorKeyRef.current}
                                bolData={bolData}
                                onDataChange={handleFormChange}
                                taskGroupType={taskGroupType}
                            />
                        ) : (
                            <BolFormEditor
                                key={editorKeyRef.current}
                                bolData={bolData}
                                onDataChange={handleFormChange}
                                taskGroupType={taskGroupType}
                            />
                        )}
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default BolPreviewModal;
